import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../Main';

const useForm = (props) => {
  let history = useHistory();
  const { isAuth } = useContext(AuthContext);
  const { t } = useTranslation();
  const [book, setBook] = useState({});
  const [title, setTitle] = useState('');
  const [bookFile, setBookFile] = useState(null);

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [newPageNumber, setNewPageNumber] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  function onDocumentLoadSuccess({ numPages }) {
    console.log('🔍 useForm - onDocumentLoadSuccess called with numPages:', numPages);
    console.log('🔍 useForm - This is the actual number of pages in the PDF file');
    setNumPages(numPages);
  }

  function changePage(offset) {
    setPageNumber((prevPageNumber) => {
      const next = prevPageNumber + offset;
      setNewPageNumber(next);
      return next;
    });
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  const jumpToPage = (value) => {
    const n = parseInt(value, 10);
    if (!n || n < 1) return;
    const max = numPages || n;
    const next = Math.min(max, n);
    setPageNumber(next);
    setNewPageNumber(next);
  };

  const goPage = (e) => {
    e.preventDefault();
    jumpToPage(newPageNumber);
  };

  useEffect(() => {
    getBookFile();
  }, []);

  const backToDetails = () => {
    saveReadPage();
    history.push('/book-details/' + props.match.params.id);
  };

  const getBookFile = () => {
    setLoading(true);
    console.log('🔍 useForm - getBookFile called, isAuth:', isAuth);
    console.log('🔍 useForm - PDF endpoint:', `/pdf${isAuth ? '' : '/public'}/${props.match.params.id}`);

    AxiosInstance.get(`/book${isAuth ? '' : '/public'}/${props.match.params.id}`).then((response) => {
      console.log('🔍 useForm - Book metadata response:', response.data);
      setBook(response.data);
      setTitle(response.data.title);
      if (response.data.bookView) setPageNumber(response.data.bookView ? response.data.bookView : 1);
    });

    AxiosInstance.get(`/pdf${isAuth ? '' : '/public'}/${props.match.params.id}`, { responseType: 'arraybuffer' })
      .then((response) => {
        console.log('🔍 useForm - PDF response received:', response);
        console.log('🔍 useForm - PDF response.data:', response.data);
        console.log('🔍 useForm - PDF response.data type:', typeof response.data);
        console.log('🔍 useForm - PDF response.data byteLength:', response.data?.byteLength);
        setBookFile(response);
      })
      .catch((error) => {
        console.log('🔍 useForm - PDF request failed:', error);
        console.log('🔍 useForm - Error details:', error.response);
        setError('ERROR_LOADING_PDF');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    window.addEventListener('beforeunload', saveReadPage);
    return () => {
      if (history.action === 'POP') saveReadPage();
      window.removeEventListener('beforeunload', saveReadPage);
    };
  }, [pageNumber, history]);

  const saveReadPage = async () => {
    AxiosInstance.post('/book/' + props.match.params.id + '/progress', {
      page: pageNumber,
    });
  };

  useEffect(() => {
    // Check if user is from UFC (Université de la Formation Continue)
    const isUFCStudent = props.user?.student?.establishment?.name && (
      props.user.student.establishment.name.includes('U.F.C') || 
      props.user.student.establishment.name.includes('Université de la Formation Continue') ||
      props.user.student.establishment.name.includes('Formation Continue') ||
      props.user.student.establishment.name.includes('UFC')
    );
    
    // Free public titles (رقم النشر 6665–6668): never show the paywall modal
    const FREE_FULL_PUBLICATION_NUMBERS = [6665, 6666, 6667, 6668];
    const isFreeFullBook = FREE_FULL_PUBLICATION_NUMBERS.includes(Number(book?.publicationNumber));

    // PRIORITY 1: Check if student has full access via any method
    // Access types:
    // 1. verified_and_paid - paid via Progres API
    // 2. verified_access - contract-based access (establishment contract)
    // 3. hasFullLibraryAccess: true - any method that grants full access
    const hasFullAccess = isFreeFullBook ||
                         props.user?.student?.subscriptionStatus === 'verified_and_paid' || 
                         props.user?.student?.subscriptionStatus === 'verified_access' ||
                         props.user?.student?.hasFullLibraryAccess === true;
    
    // Only check establishment contract if student doesn't have full access
    const shouldCheckEstablishment = !hasFullAccess && !props.user?.isEstablishmentActivated && !isUFCStudent;
    
    console.log('🔍 useForm - Access check:');
    console.log('  - subscriptionStatus:', props.user?.student?.subscriptionStatus);
    console.log('  - hasFullLibraryAccess:', props.user?.student?.hasFullLibraryAccess);
    console.log('  - hasFullAccess:', hasFullAccess);
    console.log('  - isFreeFullBook:', isFreeFullBook);
    console.log('  - shouldCheckEstablishment:', shouldCheckEstablishment);
    console.log('  - pageNumber:', pageNumber, 'numPages:', numPages);
    if (
      !isFreeFullBook &&
      pageNumber === numPages &&
      (!props.user?.isActivated || shouldCheckEstablishment)
    ) {
      setShowModal(true);
    }
  }, [pageNumber, numPages, book, props.user]);

  return {
    book,
    backToDetails,
    title: title,
    bookFile,
    numPages,
    pageNumber,
    onDocumentLoadSuccess,
    previousPage,
    nextPage,
    goPage,
    newPageNumber,
    setNewPageNumber,
    jumpToPage,
    showModal,
    setShowModal,
    loading,
    error,
    setError,
  };
};

export default useForm;
